import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { Permissao } from '@wms/shared';
import { api } from '@/lib/api';

/**
 * Administração de acesso — a API existe (rotas `/api/admin/acessos`,
 * `/api/admin/papeis`, `/api/admin/usuarios/pbs`), todas exigindo
 * `admin.total`.
 *
 * A constante fica porque o resto da tela ainda a consulta para decidir o que
 * habilitar; hoje ela é `true` e não esconde mais nada.
 */
export const API_ADMIN_PRONTA = true;

export interface UsuarioPbs {
  usuario: number;
  login: string;
  nome: string;
  /** Já tem acesso liberado ao WMS? */
  liberado: boolean;
}

export interface PapelWms {
  papelId: number;
  nome: string;
  descricao: string | null;
  permissoes: Permissao[];
}

export interface AcessoUsuario {
  usuario: number;
  login: string;
  nome: string;
  ativo: boolean;
  adminMaster: boolean;
  /** Papéis atribuídos — é por eles que a permissão é concedida. */
  papeis: number[];
  /** Efetivas, calculadas pela união dos papéis. Somente leitura na tela. */
  permissoes: Permissao[];
  /** Empresa libera os centros dela. */
  empresas: number[];
  /** Centros avulsos, somados aos que as empresas já liberam. */
  centros: number[];
  /** Crachá do PBS vinculado — é por ele que o ERP fecha conferência e NF-e. */
  cracha: string | null;
  atualizadoEm?: string | null;
}

/** O que o formulário envia — permissões não entram, papéis sim. */
export interface AcessoParaSalvar {
  usuario: number;
  ativo: boolean;
  adminMaster: boolean;
  papeis: number[];
  empresas: number[];
  centros: number[];
  /** Ausente não mexe; `null` desvincula; string vincula. */
  cracha?: string | null;
}

export interface CrachaPbs {
  cracha: string;
  nome: string | null;
  usuario: number | null;
  usuarioNome: string | null;
  impressoraNfe: string | null;
}

export function useCrachas(): UseQueryResult<CrachaPbs[], unknown> {
  return useQuery<CrachaPbs[]>({
    queryKey: ['admin', 'crachas'],
    queryFn: () => api.get<CrachaPbs[]>('/api/admin/crachas'),
    staleTime: 10 * 60_000,
  });
}

export function useBuscarUsuariosPbs(termo: string): UseQueryResult<UsuarioPbs[], unknown> {
  const limpo = termo.trim();
  return useQuery<UsuarioPbs[]>({
    queryKey: ['admin', 'usuarios-pbs', limpo],
    queryFn: () => api.get<UsuarioPbs[]>('/api/admin/usuarios/pbs', { q: limpo }),
    enabled: API_ADMIN_PRONTA && limpo.length >= 2,
  });
}

export function useAcessosWms(): UseQueryResult<AcessoUsuario[], unknown> {
  return useQuery<AcessoUsuario[]>({
    queryKey: ['admin', 'acessos'],
    queryFn: () => api.get<AcessoUsuario[]>('/api/admin/acessos'),
    enabled: API_ADMIN_PRONTA,
  });
}

export function usePapeis(): UseQueryResult<PapelWms[], unknown> {
  return useQuery<PapelWms[]>({
    queryKey: ['admin', 'papeis'],
    queryFn: () => api.get<PapelWms[]>('/api/admin/papeis'),
    // Papéis são do sistema e praticamente não mudam.
    staleTime: 30 * 60_000,
  });
}

export function useSalvarAcesso() {
  const cliente = useQueryClient();
  return useMutation<AcessoUsuario, unknown, AcessoParaSalvar>({
    mutationFn: ({ usuario, ...dados }) =>
      api.post<AcessoUsuario>(`/api/admin/acessos/${usuario}`, dados),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['admin', 'acessos'] });
      // A busca do PBS mostra "já liberado": liberar alguém muda aquela lista.
      void cliente.invalidateQueries({ queryKey: ['admin', 'usuarios-pbs'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Porta-palete por centro — esta parte da API EXISTE (WMS.CENTRO_PORTA_PALETE)
// ---------------------------------------------------------------------------

export interface CentroPortaPalete {
  centroEstoque: number;
  nome: string;
  empresa: number;
  tipoEstoque: number;
  controlaLocalizador: boolean;
  /** Medidas em CENTÍMETROS — a unidade da trena e a do cadastro de produto. */
  larguraCm: number;
  profundidadeCm: number;
  alturaCm: number;
  volumeM3: number;
  /** `false` = está usando o padrão do sistema, ninguém mediu este CD ainda. */
  cadastrada: boolean;
  observacao: string | null;
  atualizadoEm: string | null;
  /**
   * Se as posições deste centro entram na ocupação consolidada da empresa.
   *
   * Recebimento e trânsito têm posição e saldo, mas é mercadoria de passagem:
   * somá-los faz o galpão parecer mais cheio do que está. Sem cadastro, `true`.
   */
  contabilizaPosicoes: boolean;
  observacaoContabiliza: string | null;
}

export interface CentrosPortaPalete {
  centros: CentroPortaPalete[];
  padrao: { larguraCm: number; profundidadeCm: number; alturaCm: number };
}

export function useCentrosPortaPalete(
  habilitado: boolean,
): UseQueryResult<CentrosPortaPalete, unknown> {
  return useQuery<CentrosPortaPalete>({
    queryKey: ['admin', 'centros-porta-palete'],
    queryFn: () => api.get<CentrosPortaPalete>('/api/admin/centros'),
    enabled: habilitado,
    staleTime: 60_000,
  });
}

/**
 * Liga ou desliga a contagem de posições do centro na ocupação da empresa.
 *
 * Invalida a consolidação junto: a tela de Início soma exatamente os centros
 * marcados aqui, e deixá-la com o número velho depois de mudar o cadastro é a
 * forma mais rápida de ninguém mais confiar no total.
 */
export function useContabilizarCentro() {
  const cliente = useQueryClient();
  return useMutation<unknown, unknown, { centro: number; contabiliza: boolean }>({
    mutationFn: ({ centro, contabiliza }) =>
      api.put(`/api/admin/centros/${centro}/contabilizar`, { contabiliza }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['admin', 'centros-porta-palete'] });
      void cliente.invalidateQueries({ queryKey: ['ocupacao-empresa'] });
    },
  });
}

/**
 * O de-para empresa ↔ conta/depósito do Mercado Livre.
 *
 * Alimenta o painel de Expedição ML: sem ele, a tela não sabe de quem é cada
 * pedido — e um pedido que ainda não virou televenda só tem o `node` do ML
 * para se identificar.
 */
export interface GalpaoMeliAdmin {
  empresa: number;
  /** As EMPRESAS que este painel soma — um CD pode atender mais de uma. */
  empresas: number[];
  loja: number;
  warehouse: string | null;
  node: string | null;
  nome: string | null;
}

export function useGalpoesMeliAdmin(
  habilitado: boolean,
): UseQueryResult<{ galpoes: GalpaoMeliAdmin[] }, unknown> {
  return useQuery<{ galpoes: GalpaoMeliAdmin[] }>({
    queryKey: ['admin', 'meli-galpoes'],
    queryFn: () => api.get<{ galpoes: GalpaoMeliAdmin[] }>('/api/admin/meli/galpoes'),
    enabled: habilitado,
    staleTime: 60_000,
  });
}

/** Invalida também o painel: mudar o de-para muda de quem é cada pedido. */
function aoMudarGalpao(cliente: ReturnType<typeof useQueryClient>): void {
  void cliente.invalidateQueries({ queryKey: ['admin', 'meli-galpoes'] });
  void cliente.invalidateQueries({ queryKey: ['meli-galpoes'] });
  void cliente.invalidateQueries({ queryKey: ['meli-expedicao'] });
}

export function useSalvarGalpaoMeli() {
  const cliente = useQueryClient();
  return useMutation<unknown, unknown, GalpaoMeliAdmin>({
    mutationFn: (g) => api.put('/api/admin/meli/galpoes', g),
    onSuccess: () => aoMudarGalpao(cliente),
  });
}

export function useRemoverGalpaoMeli() {
  const cliente = useQueryClient();
  return useMutation<unknown, unknown, number>({
    mutationFn: (empresa) => api.del(`/api/admin/meli/galpoes/${empresa}`),
    onSuccess: () => aoMudarGalpao(cliente),
  });
}

export interface MedidaPortaPalete {
  centro: number;
  larguraCm: number;
  profundidadeCm: number;
  alturaCm: number;
  observacao?: string | null;
}

export function useSalvarPortaPalete() {
  const cliente = useQueryClient();
  return useMutation<unknown, unknown, MedidaPortaPalete>({
    mutationFn: ({ centro, ...medidas }) =>
      api.put(`/api/admin/centros/${centro}/porta-palete`, medidas),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['admin', 'centros-porta-palete'] });
      // O tamanho da vaga é o denominador do % de ocupação: mapa, relatórios e
      // dashboard precisam recalcular, senão a tela continua com o número velho.
      void cliente.invalidateQueries({ queryKey: ['mapa3d'] });
      void cliente.invalidateQueries({ queryKey: ['rel-localizadores'] });
    },
  });
}

export function useRestaurarPortaPalete() {
  const cliente = useQueryClient();
  return useMutation<unknown, unknown, number>({
    mutationFn: (centro) => api.del(`/api/admin/centros/${centro}/porta-palete`),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['admin', 'centros-porta-palete'] });
      void cliente.invalidateQueries({ queryKey: ['mapa3d'] });
      void cliente.invalidateQueries({ queryKey: ['rel-localizadores'] });
    },
  });
}

/* -------------------------------------------------------------------------
   Tarefas agendadas (Administração > Tarefas).
   ------------------------------------------------------------------------- */

export interface TarefaAdmin {
  id: number;
  nome: string;
  descricao: string | null;
  tipo: 'URL' | 'INTERNA';
  url: string | null;
  chave: string | null;
  parametros: Record<string, string | number>;
  /** `null` = todas as empresas. */
  empresas: number[] | null;
  cron: string;
  ativa: boolean;
  timeoutSegundos: number;
  proximaExecucao: string | null;
  ultimaExecucao: string | null;
  ultimoStatus: 'OK' | 'ALERTA' | 'ERRO' | null;
  ultimoResumo: string | null;
  ultimaDuracaoMs: number | null;
  executando: boolean;
}

export interface ExecucaoTarefa {
  id: number;
  inicio: string;
  duracaoMs: number | null;
  status: 'OK' | 'ALERTA' | 'ERRO';
  http: number | null;
  resumo: string | null;
  origem: 'AGENDA' | 'MANUAL';
  usuario: number | null;
}

export interface DadosTarefa {
  /** Rotina do catálogo; só na criação (a rotina de uma tarefa não muda). */
  chave?: string;
  nome: string;
  descricao: string | null;
  parametros: Record<string, string | number>;
  /** `null` = todas as empresas. */
  empresas: number[] | null;
  cron: string;
  ativa: boolean;
  timeoutSegundos: number;
}

type ListaTarefas = { tarefas: TarefaAdmin[] };

export interface ParametroRotina {
  nome: string;
  rotulo: string;
  tipo: 'numero' | 'texto' | 'url';
  obrigatorio: boolean;
  padrao?: string | number;
  ajuda?: string;
  min?: number;
  max?: number;
}

/** Uma rotina do catálogo. `chave = 'url'` é a ponte "Chamar URL externa". */
export interface RotinaCatalogo {
  chave: string;
  nome: string;
  descricao: string;
  cronPadrao: string;
  exigeEscrita: boolean;
  /** A tarefa padrão nasce sozinha; a última dela não se exclui. */
  criarPadrao: boolean;
  /** A tarefa pode ser limitada a empresas. */
  porEmpresa: boolean;
  /** O que a rotina faz, passo a passo — só leitura. */
  passos: string[];
  /** Onde a lógica mora no repositório. */
  codigo: string;
  parametros: ParametroRotina[];
  /** Quantas tarefas já existem desta rotina. */
  tarefas: number;
}

export function useCatalogoRotinas(): UseQueryResult<{ rotinas: RotinaCatalogo[] }, unknown> {
  return useQuery<{ rotinas: RotinaCatalogo[] }>({
    queryKey: ['admin', 'tarefas', 'catalogo'],
    queryFn: () => api.get<{ rotinas: RotinaCatalogo[] }>('/api/admin/tarefas/catalogo'),
  });
}

/** Atualiza a cada 30 s: "última" e "próxima" mudam sozinhas com a agenda rodando. */
export function useTarefas(habilitado = true): UseQueryResult<ListaTarefas, unknown> {
  return useQuery<ListaTarefas>({
    queryKey: ['admin', 'tarefas'],
    queryFn: () => api.get<ListaTarefas>('/api/admin/tarefas'),
    refetchInterval: 30_000,
    enabled: habilitado,
  });
}

export function useExecucoesTarefa(
  id: number,
): UseQueryResult<{ execucoes: ExecucaoTarefa[] }, unknown> {
  return useQuery<{ execucoes: ExecucaoTarefa[] }>({
    queryKey: ['admin', 'tarefas', id, 'execucoes'],
    queryFn: () => api.get<{ execucoes: ExecucaoTarefa[] }>(`/api/admin/tarefas/${id}/execucoes`),
    refetchInterval: 30_000,
  });
}

function aoMudarTarefas(cliente: ReturnType<typeof useQueryClient>, dados: ListaTarefas): void {
  cliente.setQueryData(['admin', 'tarefas'], { tarefas: dados.tarefas });
  void cliente.invalidateQueries({ queryKey: ['admin', 'tarefas'] });
}

export function useSalvarTarefa() {
  const cliente = useQueryClient();
  return useMutation<ListaTarefas, unknown, { id: number | null; dados: DadosTarefa }>({
    mutationFn: ({ id, dados }) =>
      id === null
        ? api.post<ListaTarefas>('/api/admin/tarefas', dados)
        : api.put<ListaTarefas>(`/api/admin/tarefas/${id}`, dados),
    onSuccess: (d) => aoMudarTarefas(cliente, d),
  });
}

export function useAtivarTarefa() {
  const cliente = useQueryClient();
  return useMutation<ListaTarefas, unknown, { id: number; ativa: boolean }>({
    mutationFn: ({ id, ativa }) => api.post<ListaTarefas>(`/api/admin/tarefas/${id}/ativa`, { ativa }),
    onSuccess: (d) => aoMudarTarefas(cliente, d),
  });
}

export function useExecutarTarefa() {
  const cliente = useQueryClient();
  return useMutation<ListaTarefas & { execucao: ExecucaoTarefa }, unknown, number>({
    mutationFn: (id) => api.post(`/api/admin/tarefas/${id}/executar`),
    onSuccess: (d) => aoMudarTarefas(cliente, d),
  });
}

export function useExcluirTarefa() {
  const cliente = useQueryClient();
  return useMutation<ListaTarefas, unknown, number>({
    mutationFn: (id) => api.del<ListaTarefas>(`/api/admin/tarefas/${id}`),
    onSuccess: (d) => aoMudarTarefas(cliente, d),
  });
}
